import Layout from "@/components/Layout";
import PermissionDenied from "@/components/PermissionDenied";
import { CatalogEntityCard, formatCatalogAuditDate } from "@/components/catalog/CatalogEntityCard";
import { GridPaginationFooter } from "@/components/catalog/GridPaginationControls";
import { HrListToolbar, type HrQuickDatePreset } from "@/components/hr/HrListToolbar";
import { ConfirmAlertDialog } from "@/components/ConfirmAlertDialog";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "@/components/ui/sonner";
import { useGridPagination } from "@/hooks/useGridPagination";
import { cn } from "@/lib/utils";
import {
  createLeaveType,
  deleteLeaveType,
  leaveTypeErrorMessage,
  listLeaveTypeRecords,
  syncLeaveTypesFromApi,
  updateLeaveType,
  type LeaveTypeRecord,
} from "@/lib/hr/leaveTypesStore";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { slugify } from "@/lib/slugify";
import { isAxiosError } from "axios";
import { endOfDay, format, isValid, parseISO, startOfDay, startOfMonth, subDays } from "date-fns";
import { CalendarIcon, Clock, Edit, Eye, Loader2, Plus, Tag, Trash2 } from "lucide-react";
import { motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

const PAGE_SIZE_OPTIONS = [20, 24, 28, 32, 40, 48, 60] as const;

function formatLeaveTypeDetailDate(iso: string): string {
  const d = parseISO(iso);
  return isValid(d) ? format(d, "PP") : "—";
}

type SortBy = "latest" | "oldest" | "name_asc" | "name_desc" | "updated_latest" | "updated_oldest";

export default function LeaveTypes() {
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canView = can(user, "leave_type_can_view");
  const canCreate = can(user, "leave_type_can_create");
  const canUpdate = can(user, "leave_type_can_update");
  const canDelete = can(user, "leave_type_can_delete");
  const [isForbidden, setIsForbidden] = useState(false);

  const [version, setVersion] = useState(0);
  const refresh = useCallback(() => setVersion((v) => v + 1), []);
  const [query, setQuery] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sortBy, setSortBy] = useState<SortBy>("latest");
  const [quickDate, setQuickDate] = useState<HrQuickDatePreset>("all");
  const [filterCreatedDate, setFilterCreatedDate] = useState<Date | undefined>(undefined);
  const [filterTitle, setFilterTitle] = useState("");
  const [filterSlug, setFilterSlug] = useState("");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<LeaveTypeRecord | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<LeaveTypeRecord | null>(null);
  const [viewType, setViewType] = useState<LeaveTypeRecord | null>(null);
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [saving, setSaving] = useState(false);
  const [slugDirty, setSlugDirty] = useState(false);
  const titleInputRef = useRef<HTMLInputElement | null>(null);

  const rows = useMemo(() => listLeaveTypeRecords(), [version]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "auth_user") setUser(getStoredAuthUser());
    };
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("storage", onStorage);
    window.addEventListener("auth_user_updated", onAuth);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth_user_updated", onAuth);
    };
  }, []);

  useEffect(() => {
    let m = true;
    void (async () => {
      setLoading(true);
      setIsForbidden(false);
      try {
        await syncLeaveTypesFromApi();
        if (m) refresh();
      } catch (e) {
        if (m && isAxiosError(e) && e.response?.status === 403) {
          setIsForbidden(true);
          return;
        }
        console.error(e);
        if (m) toast.error("Could not fetch leave types. Showing cached data if any.");
      } finally {
        if (m) setLoading(false);
      }
    })();
    return () => {
      m = false;
    };
  }, [refresh]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((t) => {
      if (filterCreatedDate) {
        const want = format(filterCreatedDate, "yyyy-MM-dd");
        const created = parseISO(t.createdAt);
        const got = isValid(created) ? format(created, "yyyy-MM-dd") : null;
        if (got !== want) return false;
      }
      if (quickDate !== "all") {
        const created = parseISO(t.createdAt);
        if (!isValid(created)) return false;
        const now = new Date();
        const end = endOfDay(now);
        const start =
          quickDate === "today" ? startOfDay(now) : quickDate === "week" ? startOfDay(subDays(now, 6)) : startOfMonth(now);
        if (created < start || created > end) return false;
      }
      if (filterTitle.trim() && !t.title.toLowerCase().includes(filterTitle.trim().toLowerCase())) return false;
      if (filterSlug.trim() && !t.slug.toLowerCase().includes(filterSlug.trim().toLowerCase())) return false;
      const matchesQuery = !q || t.title.toLowerCase().includes(q) || t.slug.toLowerCase().includes(q);
      return matchesQuery;
    });
  }, [rows, query, filterCreatedDate, quickDate, filterTitle, filterSlug]);

  const sorted = useMemo(() => {
    const items = [...filtered];
    items.sort((a, b) => {
      switch (sortBy) {
        case "oldest":
          return a.createdAt.localeCompare(b.createdAt);
        case "name_desc":
          return b.title.localeCompare(a.title);
        case "name_asc":
          return a.title.localeCompare(b.title);
        case "updated_oldest":
          return a.updatedAt.localeCompare(b.updatedAt);
        case "updated_latest":
          return b.updatedAt.localeCompare(a.updatedAt);
        case "latest":
        default:
          return b.createdAt.localeCompare(a.createdAt);
      }
    });
    return items;
  }, [filtered, sortBy]);

  const appliedFiltersCount = (filterCreatedDate ? 1 : 0) + (filterTitle.trim() ? 1 : 0) + (filterSlug.trim() ? 1 : 0);

  const resetSheetFilters = () => {
    setFilterCreatedDate(undefined);
    setFilterTitle("");
    setFilterSlug("");
  };

  const paginationKey = `${query}|${sortBy}|${quickDate}|${filterCreatedDate?.getTime() ?? ""}|${filterTitle}|${filterSlug}`;

  const {
    page,
    setPage,
    pageSize,
    setPageSize,
    totalCount,
    totalPages,
    paginatedItems: paginated,
    rangeStart,
    rangeEnd,
  } = useGridPagination(sorted, paginationKey, {
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    defaultPageSize: PAGE_SIZE_OPTIONS[0],
  });

  const startCreate = () => {
    setEditing(null);
    setTitle("");
    setSlug("");
    setSlugDirty(false);
    setOpen(true);
    queueMicrotask(() => titleInputRef.current?.focus());
  };

  const startEdit = (t: LeaveTypeRecord) => {
    setEditing(t);
    setTitle(t.title);
    setSlug(t.slug);
    setSlugDirty(true);
    setOpen(true);
    queueMicrotask(() => titleInputRef.current?.focus());
  };

  const computedSlug = useMemo(() => slugify(slug.trim() || title.trim()), [slug, title]);

  const save = async () => {
    if (!title.trim()) {
      toast.error("Title is required.");
      return;
    }
    const finalSlug = computedSlug;
    if (!finalSlug) {
      toast.error("Slug is required.");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        const res = await updateLeaveType(editing.id, { title: title.trim(), slug: finalSlug });
        toast.success(res?.message ?? "Updated.");
      } else {
        const res = await createLeaveType({ title: title.trim(), slug: finalSlug });
        toast.success(res.message);
      }
      setOpen(false);
      refresh();
    } catch (e) {
      console.error(e);
      toast.error(leaveTypeErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      const res = await deleteLeaveType(deleteTarget.id);
      toast.success(res.message);
      setDeleteTarget(null);
      refresh();
    } catch (e) {
      console.error(e);
      toast.error(leaveTypeErrorMessage(e));
    }
  };

  return isForbidden ? (
    <PermissionDenied
      title="Leave types access required"
      message="You don’t have permission to view leave types. Ask an administrator to grant leave_type_can_view_any."
    />
  ) : (
    <Layout title="Leave types">
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-display font-bold text-foreground">Leave types</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Create leave type titles. Slug is auto-generated from title.{" "}
              <Link to="/dashboard" className="text-primary hover:underline font-medium">
                Home
              </Link>
            </p>
          </div>
          {canCreate ? (
            <Button variant="premium" className="gap-2" onClick={startCreate}>
              <Plus className="w-4 h-4" /> Add leave type
            </Button>
          ) : null}
        </div>

        <Dialog open={!!viewType} onOpenChange={(o) => !o && setViewType(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Leave type details</DialogTitle>
            </DialogHeader>
            {viewType ? (
              <dl className="grid grid-cols-1 gap-3 text-sm">
                <div>
                  <dt className="text-xs text-muted-foreground">Title</dt>
                  <dd className="font-medium text-foreground">{viewType.title}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">Slug</dt>
                  <dd className="font-mono text-muted-foreground">{viewType.slug}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">Created</dt>
                  <dd className="text-muted-foreground">{formatLeaveTypeDetailDate(viewType.createdAt)}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">Last updated</dt>
                  <dd className="text-muted-foreground">{formatLeaveTypeDetailDate(viewType.updatedAt)}</dd>
                </div>
              </dl>
            ) : null}
            <DialogFooter>
              <Button variant="glass" onClick={() => setViewType(null)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <HrListToolbar
          searchPlaceholder="Search by title or slug…"
          query={query}
          onQueryChange={setQuery}
          filtersOpen={filtersOpen}
          onFiltersOpenChange={setFiltersOpen}
          appliedFiltersCount={appliedFiltersCount}
          filterSheetTitle="Filter leave types"
          filterSheetDescription="Refine by title, slug, and created date."
          onResetSheetFilters={resetSheetFilters}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          pageSizeIdPrefix="leave-types"
          sortBy={sortBy}
          onSortByChange={(v) => setSortBy(v as SortBy)}
          sortOptions={[
            { value: "latest", label: "Newest first" },
            { value: "oldest", label: "Oldest first" },
            { value: "name_asc", label: "Title: A to Z" },
            { value: "name_desc", label: "Title: Z to A" },
            { value: "updated_latest", label: "Recently updated" },
            { value: "updated_oldest", label: "Least recently updated" },
          ]}
          quickDate={quickDate}
          onQuickDateChange={setQuickDate}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          filterSheetChildren={
            <div className="rounded-xl border border-border/50 bg-muted/15 p-4 space-y-4">
              <p className="text-sm font-medium text-foreground">Leave type</p>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Title contains</Label>
                <Input className="glass-input h-10" value={filterTitle} onChange={(e) => setFilterTitle(e.target.value)} placeholder="e.g. Annual" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Slug contains</Label>
                <Input className="glass-input h-10 font-mono text-sm" value={filterSlug} onChange={(e) => setFilterSlug(e.target.value)} placeholder="e.g. annual" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Created date</Label>
                <Popover modal={true}>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className={cn("glass-input h-10 w-full justify-start text-left font-normal", !filterCreatedDate && "text-muted-foreground")}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 shrink-0 opacity-70" />
                      {filterCreatedDate ? format(filterCreatedDate, "PPP") : "Any date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="z-[100] w-auto p-0" align="start" sideOffset={6}>
                    <Calendar mode="single" selected={filterCreatedDate} onSelect={setFilterCreatedDate} initialFocus />
                    <div className="flex gap-2 border-t p-2">
                      <Button type="button" variant="ghost" size="sm" className="flex-1" onClick={() => setFilterCreatedDate(undefined)}>
                        Clear
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          }
        />

        {loading ? (
          <div className="glass-card p-8 flex items-center gap-2 text-muted-foreground text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : sorted.length === 0 ? (
          <div className="glass-card p-6 text-sm text-muted-foreground">
            {rows.length === 0
              ? canCreate
                ? "No leave types yet. Click “Add leave type” to create one for leave requests."
                : "No leave types yet."
              : "No leave types match your filters."}
          </div>
        ) : viewMode === "grid" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {paginated.map((t, i) => (
              <motion.div key={t.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }}>
                <CatalogEntityCard
                  title={t.title}
                  primaryMeta={{ label: "Slug", value: t.slug, icon: Tag, iconClassName: "text-emerald-600" }}
                  secondaryMeta={{ label: "Updated", value: formatCatalogAuditDate(t.updatedAt), icon: Clock, iconClassName: "text-sky-600" }}
                  footer={`Created ${formatCatalogAuditDate(t.createdAt)}`}
                  footerRight={formatCatalogAuditDate(t.updatedAt)}
                  onView={canView ? () => setViewType(t) : undefined}
                  onEdit={canUpdate ? () => startEdit(t) : undefined}
                  onDelete={canDelete ? () => setDeleteTarget(t) : undefined}
                />
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {paginated.map((t) => (
              <div key={t.id} className="glass-card p-4 flex flex-wrap items-center gap-3 justify-between">
                <div className="min-w-0">
                  <p className="font-semibold text-foreground">{t.title}</p>
                  <p className="text-xs text-muted-foreground font-mono mt-0.5">{t.slug}</p>
                  <p className="text-xs text-muted-foreground mt-1">Added {formatCatalogAuditDate(t.createdAt)}</p>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {canView ? (
                    <Button variant="glass" size="sm" onClick={() => setViewType(t)}>
                      <Eye className="w-3.5 h-3.5" /> View
                    </Button>
                  ) : null}
                  {canUpdate ? (
                    <Button variant="glass" size="sm" onClick={() => startEdit(t)}>
                      <Edit className="w-3.5 h-3.5" /> Edit
                    </Button>
                  ) : null}
                  {canDelete ? (
                    <Button variant="ghost" size="sm" className="text-destructive" onClick={() => setDeleteTarget(t)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && sorted.length > 0 ? (
          <GridPaginationFooter
            totalCount={totalCount}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        ) : null}

        <Dialog
          open={open}
          onOpenChange={(o) => {
            setOpen(o);
            if (!o) {
              setSlugDirty(false);
            }
          }}
        >
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Tag className="w-4 h-4" />
                {editing ? "Edit leave type" : "New leave type"}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Title *</Label>
                <Input
                  ref={titleInputRef}
                  className="glass-input h-10"
                  value={title}
                  onChange={(e) => {
                    const next = e.target.value;
                    setTitle(next);
                    if (!slugDirty) setSlug(slugify(next));
                  }}
                  placeholder="e.g. Annual leave"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Slug</Label>
                <Input
                  className="glass-input h-10 font-mono text-sm"
                  value={slug}
                  onChange={(e) => {
                    setSlugDirty(true);
                    setSlug(e.target.value);
                  }}
                  placeholder="Auto-generated"
                />
                <p className="text-[11px] text-muted-foreground">
                  Will be saved as: <span className="font-mono">{computedSlug || "—"}</span>
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button variant="glass" onClick={() => setOpen(false)} disabled={saving}>
                Cancel
              </Button>
              <Button variant="premium" onClick={() => void save()} disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <ConfirmAlertDialog
          open={Boolean(deleteTarget)}
          onOpenChange={(o) => !o && setDeleteTarget(null)}
          title="Delete leave type?"
          description={deleteTarget ? `Delete "${deleteTarget.title}"?` : ""}
          confirmLabel="Delete"
          onConfirm={() => void confirmDelete()}
        />
      </div>
    </Layout>
  );
}

